<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="print-rt-main">
    <div class="rt-options">
      <div class="rt-filters-table-controls">
        <div class="rt-filter-summary-container">
          <span>{{ rtFilterSummary }}</span>
        </div>
        <ButtonFilter
          buttonText="Change Filter"
          filterPosition="right"
          :filterWidth="420"
          :isOpen.sync="rtFilterIsOpen"
        >
          <div class="filter-content">
            <BoxContainer :title="`${rtTypePlaceholder} Date`" :isCheckBox="true" :isChecked.sync="filterByRtDate">
              <DateTimePicker
                :placeholder="`Start ${rtTypePlaceholder} Date`"
                :value.sync="rtStartDate"
                :showTime="false"
                :isRequired="filterByRtDate"
                :errorMessage.sync="rtStartDateErr"
                :isDisabled="!filterByRtDate"
              />
              <DateTimePicker
                :placeholder="`End ${rtTypePlaceholder} Date`"
                :value.sync="rtEndDate"
                :showTime="false"
                :isRequired="filterByRtDate"
                :errorMessage.sync="rtEndDateErr"
                :isDisabled="!filterByRtDate"
              />
            </BoxContainer>

            <BoxContainer :title="`${rtTypePlaceholder} No.`" :isCheckBox="true" :isChecked.sync="filterByRtNo">
              <InputQuery
                :placeholder="`Start ${rtTypePlaceholder} No.`"
                :items="rtNoList"
                :itemKey="`${rtTypePlaceholder} No`"
                :selectedObject.sync="selectedStartRtNo"
                :search.sync="startRtNoSearch"
                :isRequired="filterByRtNo"
                :errorMessage.sync="startRtNoErr"
                :isDisabled="!filterByRtNo"
                :allowFreeText="true"
                validator="text,25"
              />
              <InputQuery
                :placeholder="`End ${rtTypePlaceholder} No.`"
                :items="rtNoList"
                :itemKey="`${rtTypePlaceholder} No`"
                :selectedObject.sync="selectedEndRtNo"
                :search.sync="endRtNoSearch"
                :isRequired="filterByRtNo"
                :errorMessage.sync="endRtNoErr"
                :isDisabled="!filterByRtNo"
                :allowFreeText="true"
                validator="text,25"
              />
            </BoxContainer>

            <BoxContainer v-if="!isMisc" title="Customer" :isCheckBox="true" :isChecked.sync="filterByCustomer">
              <InputQuery
                placeholder="Start Customer"
                :items="customerList"
                itemKey="Code - Customer"
                :showDesc="false"
                :selectedObject.sync="selectedStartCustomer"
                :isRequired="filterByCustomer"
                :errorMessage.sync="startCustomerErr"
                :isDisabled="!filterByCustomer"
                :hideColumns="['Code - Customer']"
                validator="text,105"
              />
              <InputQuery
                placeholder="End Customer"
                :items="customerList"
                itemKey="Code - Customer"
                :showDesc="false"
                :selectedObject.sync="selectedEndCustomer"
                :isRequired="filterByCustomer"
                :errorMessage.sync="endCustomerErr"
                :isDisabled="!filterByCustomer"
                :hideColumns="['Code - Customer']"
                validator="text,105"
              />
            </BoxContainer>

            <BoxContainer v-if="isMisc" title="Payee" :isCheckBox="true" :isChecked.sync="filterByCustomer">
              <InputQuery
                placeholder="Payee Type"
                :items="payeeTypeList"
                itemKey="Code - Party"
                :showDesc="false"
                :selectedObject.sync="selectedPayeeType"
                :isRequired="filterByCustomer"
                :errorMessage.sync="payeeTypeErr"
                :isDisabled="!filterByCustomer"
                :hideColumns="['Code - Party']"
                validator="text,105"
                @selected="payeeTypeSelected"
              />
              <InputQuery
                placeholder="Start Payee"
                :items="payeeCodeList"
                itemKey="Code - Name"
                :showDesc="false"
                :selectedObject.sync="selectedStartPayee"
                :isRequired="filterByCustomer"
                :errorMessage.sync="startPayeeErr"
                :isDisabled="!filterByCustomer || !selectedPayeeType"
                :hideColumns="['Code - Name']"
                validator="text,105"
              />
              <InputQuery
                placeholder="End Payee"
                :items="payeeCodeList"
                itemKey="Code - Name"
                :showDesc="false"
                :selectedObject.sync="selectedEndPayee"
                :isRequired="filterByCustomer"
                :errorMessage.sync="endPayeeErr"
                :isDisabled="!filterByCustomer || !selectedPayeeType"
                :hideColumns="['Code - Name']"
                validator="text,105"
              />
            </BoxContainer>
            <div style="margin-top: 5px">
              <CheckBoxV2 title="Show Only Not Printed" :isChecked.sync="showOnlyNotPrintedIsChecked" />
            </div>
            <div class="refresh-button">
              <button @click="refreshClicked">Refresh</button>
            </div>
          </div>
        </ButtonFilter>

        <button
          class="select-deselect-all-button"
          :disabled="selectDeselectAllDisabled"
          @click="selectDeselectAllClicked"
        >
          {{ selectDeselectAllButtonText }}
        </button>
      </div>

      <div class="rt-tablebox">
        <TableBox
          :title="rtTypePlaceholder"
          :tableData.sync="rtTableData"
          :selectedRow.sync="rtSelectedRow"
          :maxSelection="-1"
          :loading="rtTableLoading"
          :hideColumns="rtTableHiddenColumns"
          :dataType="{ 'Receipt Date': 'date', Total: 'currency' }"
        />
      </div>

      <div class="header-formatting-controls">
        <CheckBoxV2 title="Show Company Letter Head" :isChecked.sync="showLetterHeadChecked" />
      </div>

      <div class="call-to-action-buttons">
        <button @click="previewReceiptClicked">Preview {{ rtTypePlaceholder }}</button>
      </div>
    </div>

    <div class="rt-preview">
      <DesignerV3
        v-if="showDesignerV3"
        :reportFiles="designerV3ReportFiles"
        :reportData="designerV3ReportData"
        :sharedData="designerV3SharedData"
        :canShare="true"
        :shareBehaviour="designerV3ShareBehaviour"
        :canClose="false"
        :downloadName="downloadFileName"
        @shareClicked="designerV3ShareClicked"
        @afterPrint="designerV3AfterPrint"
        ref="designerV3"
      />

      <div v-else class="user-instructions">
        <span
          >Select some {{ rtTypePlaceholder }} and click on <strong>Preview {{ rtTypePlaceholder }}</strong> to get
          started</span
        >
      </div>
    </div>

    <PopupV2
      class="email-rt-popup"
      :show="showEmailRtPopup"
      icon="https://dsmlib.logistics.systems/Icons/BlackMail.png"
      :buttons="emailRtPopupButtons"
      :title="`Email ${rtTypePlaceholder}`"
      width="60%"
      @buttonClicked="emailRtPopupButtonClicked"
    >
      <div class="email-rt-popup-content-container">
        <div class="mail-inputs-container">
          <div class="email-input-container">
            <EmailInput :mails.sync="emailRtPopupToMails" title="To" />
          </div>
          <div class="email-input-container">
            <EmailInput :mails.sync="emailRtPopupCCMails" title="CC" />
          </div>
          <div class="email-input-container">
            <EmailInput :mails.sync="emailRtPopupBCCMails" title="BCC" />
          </div>
        </div>
        <div class="email-content-inputs-container">
          <Input :inputValue.sync="emailRtPopupSubjectInputValue" placeholder="Subject" validator="text,100" />
          <RemarksBox title="Message" :text.sync="emailRtPopupMessageText" :maxLength="1000" />
        </div>
      </div>
    </PopupV2>
  </div>
</template>

<script>
export default {
  components: {},
  computed: {
    rtTypePlaceholder: function () {
      return this.rtType == '1' ? 'Misc. Receipt' : 'Receipt';
    },

    isMisc: function () {
      return this.rtType == '1';
    },

    selectDeselectAllDisabled: function () {
      return this.rtTableData.length == 0;
    },

    selectDeselectAllButtonText: function () {
      if (this.rtTableData.length == 0) {
        return 'Select All';
      }
      return this.rtTableData.length != this.rtSelectedRow.length ? 'Select All' : 'Deselect All';
    },

    rtTableHiddenColumns: function () {
      if (this.rtTableData.length == 0) return [];
      const allColumns = Object.keys(this.rtTableData[0]);
      return allColumns.filter(
        (key) =>
          ![
            'Receipt No',
            'Misc. Receipt No',
            'Receipt Date',
            'Misc. Receipt Date',
            'Customer No',
            'Payee Code',
            'Total',
          ].includes(key),
      );
    },
  },
  data: function () {
    return {
      userDetails: null,
      rtType: '0', // 0 - Normal RT, 1 - Misc RT

      rtFilterSummary: '',
      rtFilterIsOpen: false,

      filterByRtDate: false,
      rtStartDate: null,
      rtStartDateErr: '',
      rtEndDate: null,
      rtEndDateErr: '',

      rtNoList: [],
      filterByRtNo: false,
      selectedStartRtNo: null,
      startRtNoSearch: '',
      startRtNoErr: '',
      selectedEndRtNo: null,
      endRtNoSearch: '',
      endRtNoErr: '',

      customerList: [],
      filterByCustomer: false, // reuse for Customer and Payee selections, because only one will show
      selectedStartCustomer: null,
      startCustomerErr: '',
      selectedEndCustomer: null,
      endCustomerErr: '',
      payeeTypeList: [],
      selectedPayeeType: null,
      payeeTypeErr: '',
      payeeCodeList: [],
      payeeCodeKey: '',
      selectedStartPayee: null,
      startPayeeErr: '',
      selectedEndPayee: null,
      endPayeeErr: '',

      showOnlyNotPrintedIsChecked: false,

      // Table Box
      rtTableData: [],
      rtSelectedRow: [],
      rtTableLoading: false,

      // Report Format Controls
      showLetterHeadChecked: true,

      // SettingsSystem
      chequeNoPlacement: '0', // 0 - In header, 1 - Below total amount
      showSummaryTable: false, // Contains Description (remarks), Advance (balance amt), Amount (total amt)

      // Report DesignerV3 variables
      showDesignerV3: false,
      designerV3ReportFiles: [],
      designerV3ReportData: [],
      designerV3SharedData: [],
      designerV3ShareBehaviour: {
        behaviour: 'uploadAndEmit',
        options: {
          fileName: '',
          folder: '',
        },
      },
      downloadFileName: '',

      showEmailRtPopup: false,
      emailRtPopupButtons: [{ text: 'Send Email' }, { text: 'Cancel', color: 'lightgray' }],
      emailRtPopupToMails: [],
      emailRtPopupCCMails: [],
      emailRtPopupBCCMails: [],
      emailRtPopupSubjectInputValue: '',
      emailRtPopupMessageText: '',
    };
  },
  props: {},
  methods: {
    selectDeselectAllClicked: function () {
      if (this.rtTableData.length !== this.rtSelectedRow.length) {
        this.rtSelectedRow = [];
        this.$nextTick(() => {
          this.rtSelectedRow = [...this.rtTableData];
        });
      } else {
        this.rtSelectedRow = [];
      }
    },

    refreshClicked: async function () {
      this.rtStartDateErr = '';
      this.rtEndDateErr = '';
      this.startRtNoErr = '';
      this.endRtNoErr = '';
      this.startCustomerErr = '';
      this.endCustomerErr = '';

      let error = false;

      if (this.filterByRtDate) {
        if (!this.rtStartDate) {
          this.rtStartDateErr = `Start ${this.rtTypePlaceholder} Date is required`;
          error = true;
        }

        if (!this.rtEndDate) {
          this.rtEndDateErr = `End ${this.rtTypePlaceholder} Date is required`;
          error = true;
        } else if (this.rtStartDate > this.rtEndDate) {
          this.rtEndDateErr = `End ${this.rtTypePlaceholder} Date cannot be greater than Start ${this.rtTypePlaceholder} Date`;
          error = true;
        }
      }

      if (this.filterByRtNo) {
        if (!this.selectedStartRtNo && !this.startRtNoSearch.trim()) {
          this.startRtNoErr = `Start ${this.rtTypePlaceholder} No. is required`;
          error = true;
        }

        if (!this.selectedEndRtNo && !this.endRtNoSearch.trim()) {
          this.endRtNoErr = `End ${this.rtTypePlaceholder} No. is required`;
          error = true;
        }
      }

      if (this.filterByCustomer) {
        if (this.rtType == '1') {
          if (!this.selectedPayeeType) {
            this.payeeTypeErr = 'Payee Type is required';
          }

          if (!this.selectedStartPayee) {
            this.startPayeeErr = 'Start Payee is required';
            error = true;
          }

          if (!this.selectedEndPayee) {
            this.endPayeeErr = 'End Payee is required';
            error = true;
          }
        } else {
          if (!this.selectedStartCustomer) {
            this.startCustomerErr = 'Start Customer is required';
            error = true;
          }

          if (!this.selectedEndCustomer) {
            this.endCustomerErr = 'End Customer is required';
            error = true;
          }
        }
      }

      if (error) return;

      this.rtFilterIsOpen = false;
      this.updateRtFilterSummary();
      await this.getFilteredRtListing();
    },

    getFilteredRtListing: async function () {
      const requestBody = {
        rttype: this.rtType,
        startdate: this.filterByRtDate ? this.rtStartDate.formatString('yyyy-MM-dd') : '',
        enddate: this.filterByRtDate ? this.rtEndDate.formatString('yyyy-MM-dd') : '',
        startrtno:
          this.filterByRtNo ?
            this.selectedStartRtNo ?
              this.selectedStartRtNo[`${this.rtTypePlaceholder} No`]
            : this.startRtNoSearch
          : '',
        endrtno:
          this.filterByRtNo ?
            this.selectedEndRtNo ?
              this.selectedEndRtNo[`${this.rtTypePlaceholder} No`]
            : this.endRtNoSearch
          : '',
        startcustomer: this.rtType == '0' && this.filterByCustomer ? this.selectedStartCustomer['Customer Code'] : '',
        endcustomer: this.rtType == '0' && this.filterByCustomer ? this.selectedEndCustomer['Customer Code'] : '',
        payeetype: this.rtType == '1' && this.filterByCustomer ? this.selectedPayeeType['Party Code'] : '',
        startpayee: this.rtType == '1' && this.filterByCustomer ? this.selectedStartPayee[this.payeeCodeKey] : '',
        endpayee: this.rtType == '1' && this.filterByCustomer ? this.selectedEndPayee[this.payeeCodeKey] : '',
        shownotprinted: this.showOnlyNotPrintedIsChecked ? '1' : '0',
      };

      this.rtTableData = [];
      this.rtSelectedRow = [];
      this.rtTableLoading = true;

      const rtListing = await this.$apiCallV2('/PrintReceipt/GetReceiptListing', requestBody);
      this.rtTableData = rtListing
        .filter((rt) => rt['Indicator'] != 'C')
        .map((rt, i) => {
          return {
            ...rt,
            '#': i + 1,
          };
        });
      this.rtTableLoading = false;
    },

    updateRtFilterSummary: function () {
      const filterList = [];

      if (this.filterByRtDate) {
        filterList.push(
          `date from ${this.rtStartDate.formatString('yyyy-MM-dd')} to ${this.rtEndDate.formatString('yyyy-MM-dd')}`,
        );
      }

      if (this.filterByRtNo) {
        filterList.push(
          `${this.rtTypePlaceholder} No. from ${this.selectedStartRtNo ? this.selectedStartRtNo[`${this.rtTypePlaceholder} No`] : this.startRtNoSearch} to ${this.selectedEndRtNo ? this.selectedEndRtNo[`${this.rtTypePlaceholder} No`] : this.endRtNoSearch}`,
        );
      }

      if (this.filterByCustomer && this.rtType == '0') {
        filterList.push(
          `Customer No. from ${this.selectedStartCustomer['Customer Code'] ?? ''} to ${this.selectedEndCustomer['Customer Code'] ?? ''}`,
        );
      } else if (this.filterByCustomer && this.rtType == '1') {
        filterList.push(
          `${this.selectedPayeeType['Party Code']} No. from ${this.selectedStartPayee[this.payeeCodeKey] ?? ''} to ${this.selectedEndPayee[this.payeeCodeKey] ?? ''}`,
        );
      }

      this.rtFilterSummary = `Filter by ${filterList.join(', ')}`;
    },

    payeeTypeSelected: async function (payeeType) {
      this.$fullscreenLoading('Getting Payee Codes');
      const payeeCodes =
        payeeType ?
          await this.$apiCallV2('/Master/GetDetailsMaster', {
            masterfile: 'payeecode',
            recordid: payeeType['Table'],
          })
        : [];
      if (payeeCodes.length > 0) {
        const codeKeys = Object.keys(payeeCodes[0]);
        this.payeeCodeKey = codeKeys[0];
        // this.payeeCodeDescKey = codeKeys[1];

        this.payeeCodeList = payeeCodes.map((payee) => {
          return {
            ...payee,
            'Code - Name': `${payee[codeKeys[0]]} - ${payee[codeKeys[1]]}`,
          };
        });
      }
      this.$fullscreenLoading();
    },

    previewReceiptClicked: async function () {
      if (this.rtSelectedRow.length == 0) {
        this.$showAlert(
          'error',
          `No ${this.rtTypePlaceholder} Selected`,
          `Please select at least one ${this.rtTypePlaceholder.toLowerCase()} to proceed`,
        );
        return;
      }

      const requestBody = {
        receiptnoarray: this.rtSelectedRow.map((rt) => rt[`${this.rtTypePlaceholder} No`]).join(','),
        ismisc: this.rtType,
        showletterhead: this.showLetterHeadChecked ? '1' : '0',
      };

      this.$fullscreenLoading(`Getting ${this.rtTypePlaceholder}`);
      const results = await this.$apiCallV2('/PrintReceipt/PrintRT', requestBody);
      if (Array.isArray(results) && results[0].Results === '1') {
        this.$showAlert('error', `Error Printing ${this.rtTypePlaceholder}`, results[0].ErrMsg[{ texxt: 'OK' }]);
        this.$fullscreenLoading();
        return;
      }
      const rtDetails = results['Receipt'];

      rtDetails['General']['Show Company Letter Head'] = rtDetails['General']['Show Company Letter Head'] != '0';
      rtDetails['General']['companyheader'] = this.userDetails['CoHeaderFin'] ? this.userDetails['CoHeaderFin'] : rtDetails['General']['companyheader'];

      this.$fullscreenLoading('Preparing to Print');
      for (const rt of rtDetails['ReceiptHeader']) {
        rt['printDate'] = new Date().formatString(this.$store.state['preferredDateFormat']);
        rt['page'] = '{{jsreportPageNum}} of {{jsreportTotalPage}}';

        const addr = rt['addr'].split('\n').filter((adr) => adr.trim() != '');
        if (rt['postcode'].trim() != '' || rt['city'].trim() != '') {
          addr.push(`${rt['postcode']} ${rt['city']}`);
        }
        if (rt['state'].trim() != '' || rt['country'].trim() != '') {
          addr.push(`${rt['state']} ${rt['country']}`);
        }

        for (const [i, val] of addr.entries()) {
          if (i > 5) {
            rt['addrLine6'] += `<br>${val}`;
            continue;
          }
          rt[`addrLine${i + 1}`] = val;
        }

        if (rt['remarks1'] || rt['remarks2']) {
          const remarksCombine = [rt['remarks1'], rt['remarks2']].filter((r) => r.trim() != '').join(' ');
          rt['remarks'] = `Remark: ${remarksCombine}`;
        }

        rt['receiptdate'] = new Date(rt['receiptdate']).formatString(this.$store.state['preferredDateFormat']) || '';
        rt['totalAmt'] = this.$currencyFormat(rt['_amt']);
        rt['_showDetails'] = rt['consistOffset'] == '1';
        rt['_showSummaryTable'] = this.showSummaryTable;
        rt['balanceAmt'] = this.$currencyFormat(rt['_bal']);

        switch (this.chequeNoPlacement) {
          case '1': // Below details
            rt['_showChequeNoBelowDetails'] = true;
            if (rt['paymenttype'] == 'CS') {
              // strikethrough Cheque
              rt['cashchqLabel'] = 'Cash / <s>Cheque</s> #:';
            } else {
              // strikthrough Cash
              rt['cashchqLabel'] = '<s>Cash</s>/Cheque #:';
            }
            break;

          default: // 0(in header). Have to do this way because cannot have conditional rendering inside table for specific cell
            rt['chqPlaceholder'] = 'Cheque No';
            rt['colon'] = ':';
            rt['chequeNo'] = rt['chequeno'];
            break;
        }

        if (rt['ReceiptDetails'] && rt['ReceiptDetails'].length > 0) {
          rt['_receiptDetails'] = rt['ReceiptDetails'].map((det) => {
            return {
              docNo: det['docno'],
              docDate: new Date(det['docdate']).formatString(this.$store.state['preferredDateFormat']) || '',
              docAmt: this.$currencyFormat(det['_docamt'] || 0),
              docOffset: this.$currencyFormat(det['_docoffset'] || 0),
            };
          });
        }
      }
      this.$fullscreenLoading();

      this.designerV3ReportFiles = Array.from({ length: rtDetails['ReceiptHeader'].length }, () => 'Receipt.json');
      this.designerV3ReportData = rtDetails['ReceiptHeader'];
      this.designerV3SharedData = rtDetails.General;
      this.downloadFileName = `Receipt_${rtDetails['ReceiptHeader'][0]['receiptno']}`;
      this.showDesignerV3 = true;
    },

    designerV3ShareClicked: function (shareLink) {
      if (this.designerV3ReportData[0].email) {
        this.emailRtPopupToMails = this.designerV3ReportData[0].email.split(';').map((email) => ({
          email: email.trim(),
          valid: true,
        }));
      } else {
        this.emailRtPopupToMails = [];
      }
      this.emailRtPopupCCMails = [];
      this.emailRtPopupBCCMails = [];

      this.emailRtPopupSubjectInputValue = this.rtType == '1' ? 'Miscellaneos Receipt' : 'Receipt';
      this.emailRtPopupMessageText = `${this.rtType == '1' ? 'Miscellaneos Receipt' : 'Receipt'}: ${shareLink}\n\n`;

      this.showEmailRtPopup = true;
    },

    designerV3AfterPrint: async function () {
      const requestBody = {
        report: 'INVOICE',
        coshortname: this.userDetails.CoRefname,
        invoices: this.designerV3ReportData.map((reportData) => ({
          recordid: reportData.receiptno,
          invtype: 'RT',
        })),
      };
      const response = await this.$apiCallV2('/PrintReceipt/UpdatePrintCount', requestBody);
      if (response !== '') {
        this.$showAlert(
          'error',
          'Fail to update print count',
          response?.ErrMsg.toString() ?? 'An error occured when trying to update print count',
          [{ text: 'OK' }],
        );
      }
    },

    emailRtPopupButtonClicked: async function (buttonText) {
      if (buttonText === 'Cancel') {
        this.showEmailRtPopup = false;
        return;
      }

      if (this.emailRtPopupToMails.filter((mail) => mail.valid).length === 0) {
        this.$showAlert('error', 'No Valid Email', "Please enter at least one valid 'To' email address", [
          { text: 'OK' },
        ]);
        return;
      }

      if (!this.emailRtPopupSubjectInputValue.trim()) {
        const proceedWithoutSubject = await this.$showAlert(
          'warning',
          'No Subject',
          'No subject was entered\nProceed to email without a subject?',
          [{ text: 'Proceed' }, { text: 'Cancel', color: 'lightgray' }],
        );

        if (proceedWithoutSubject === 'Cancel') return;
      }

      const requestBody = {
        emaillist: [
          {
            doctype: this.rtType == '1' ? 'MiscReceipt' : 'Receipt',
            docno: this.designerV3ReportData[0]['receiptno'],
            docnofieldname: '',
            recipient: this.emailRtPopupToMails.map((mail) => mail.email).join(';'),
            cc: this.emailRtPopupCCMails.map((mail) => mail.email).join(';'),
            bcc: this.emailRtPopupBCCMails.map((mail) => mail.email).join(';'),
            subject: this.emailRtPopupSubjectInputValue.trim(),
            message: this.emailRtPopupMessageText,
          },
        ],
      };

      this.$fullscreenLoading('Sending Email');
      const sendEmailResponse = await this.$apiCallV2('/PrintReceipt/SendEmail', requestBody);
      this.$fullscreenLoading();

      if (sendEmailResponse?.trim?.()?.endsWith?.('queue to send.')) {
        this.$showAlert('success', 'Email successfully sent', 'Your email has been successfully sent', [
          { text: 'OK' },
        ]);
        this.showEmailRtPopup = false;
      } else {
        this.$showAlert('error', 'Email not sent', 'Your email could not be sent. Please try again.', [{ text: 'OK' }]);
      }
    },
  },
  async mounted() {
    this.rtType = this.$route.query.type?.trim?.() || '0';

    const currentDate = new Date();
    let startDate = new Date(currentDate);
    startDate.setDate(-180);
    startDate = startDate.formatString('yyyy-MM-dd');
    const endDate = currentDate.formatString('yyyy-MM-dd');
    const dataToPost = {
      startdate: startDate,
      enddate: endDate,
      rttype: this.rtType,
    };

    const mountedPromises = [];
    let mountedPromisesFulFilledCount = 0;

    mountedPromises.push(
      this.$apiCallV2('/PrintReceipt/GetReceiptListing', dataToPost).then((result) => {
        this.rtNoList = result.filter((rt) => rt['Indicator'] != 'C');
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'BILLCUST' }).then((result) => {
        this.customerList = result.map((c) => {
          return {
            ...c,
            'Code - Customer': `${c['Customer Code']} - ${c['Customer Name']}`,
          };
        });
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'PARTY' }).then((result) => {
        this.payeeTypeList = result.map((p) => {
          return {
            ...p,
            'Code - Party': `${p['Party Code']} - ${p['Party']}`,
          };
        });
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=6G_RTChqPlacement_FP').then((result) => {
        this.chequeNoPlacement = result[0].SettingsValue;
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=6G_RTShowSummary_FP').then((result) => {
        this.showSummaryTable = result[0].SettingsValue == '1';
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Login/GetDetails').then((result) => {
        this.userDetails = result;
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    // Set start rt no if route contains rtno
    const queryParamDocumentNo = this.$route.query.rtno?.trim?.();
    if (queryParamDocumentNo) {
      this.filterByRtNo = true;
      this.startRtNoSearch = queryParamDocumentNo;
      this.endRtNoSearch = queryParamDocumentNo;
    } else {
      this.filterByRtDate = true;
      this.rtStartDate = new Date();
      this.rtEndDate = new Date();
    }

    this.updateRtFilterSummary();
    const getFilteredRtListPromise = this.getFilteredRtListing();

    await Promise.allSettled([...mountedPromises, getFilteredRtListPromise]).then((_promiseOutcomes) => {
      if (queryParamDocumentNo) {
        const matchingRt = this.rtNoList.find((rt) => rt[`${this.rtTypePlaceholder} No`] == queryParamDocumentNo);
        if (matchingRt) {
          this.selectedStartRtNo = matchingRt;
          this.selectedEndRtNo = matchingRt;
        }
      }
    });

    if (this.rtTableData.length == 1) {
      this.rtSelectedRow = [...this.rtTableData];
    }
  },
  watch: {},
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.print-rt-main {
  font-size: 12px;
  width: 100%;
  height: 100%;
  padding: 10px 45px;
  display: flex;

  > .rt-options {
    margin-right: 2.5px;
    flex: 1;
    background-color: white;
    padding: 5px;

    > .rt-filters-table-controls {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      margin-bottom: 5px;

      > .rt-filter-summary-container {
        margin-right: 5px;
        align-content: center;
        text-align: end;
      }

      .filter-content {
        text-align: left;
        cursor: auto;

        .refresh-button {
          margin-top: 10px;
          display: flex;
          justify-content: flex-end;
        }
      }

      > .select-deselect-all-button {
        margin-left: 5px;
      }
    }

    > .rt-tablebox {
      height: calc(100% - 150px);
    }

    > .header-formatting-controls {
      margin-top: 5px;
      width: 100%;
    }

    > .call-to-action-buttons {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      margin-top: 15px;
      padding-bottom: 5px;
    }
  }

  > .rt-preview {
    margin-left: 2.5px;
    flex: 1;
    background-color: white;
    padding: 5px;

    > .user-instructions {
      height: 100%;
      display: flex;
      text-align: center;
      justify-content: center;
      align-items: center;
      font-size: 20px;
    }
  }

  > .email-rt-popup {
    .email-rt-popup-content-container {
      display: flex;
      flex-direction: row;

      > .mail-inputs-container {
        margin-right: 5px;
        display: flex;
        flex-direction: column;
        flex: 1;

        > .email-input-container {
          margin-bottom: 5px;
        }
      }

      > .email-content-inputs-container {
        margin-left: 5px;
        display: flex;
        flex-direction: column;
        flex: 2;
      }
    }
  }
}
</style>
